// Clemson University: public college page (#2840), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Clemson's Common Data Set 2025-2026, a scanned PDF in Clemson OPEN (the
//    library's repository, open.clemson.edu, linked from the Office of
//    Institutional Research's data reports page), cited by its record page
//    (https://open.clemson.edu/cds/18). The PDF sits behind a browser
//    challenge, so it was saved through a real browser and each figure was
//    read off the page images: C1 on PDF pages 14 and 15 (Fall 2025, with
//    the residency table filled in), C8 on page 18 (test policy for Fall
//    2027), C13/C14 on page 23, C21/C22 on page 24. Quirk: the admitted and
//    enrolled figures used here are the C1 residency table's TOTAL column
//    (27,498 and 5,084), which Clemson's Admissions Statistics page also
//    gives; the C1 rows by sex sum to 27,497 admitted and 5,085 enrolled,
//    one off each way. Applied (64,805) agrees in both. G1 and G5 are blank:
//    Clemson checked the box saying its 2026-2027 costs were not yet
//    available, so the cost table comes from the financial aid office.
//  - Clemson Admissions, Apply to Clemson as a First-Year Student: the
//    application schedule (Early Action, Regular Decision, final date to
//    apply), test-optional policy and superscoring. The page carries no
//    year; it is the live page for the cycle that opened August 1, 2026
//    (fall 2027 entry), and its October 15 Early Action and May 1 closing
//    dates match the CDS (C14, C22), whose admission policy questions are
//    asked for the Fall 2027 cycle.
//  - Clemson Admissions, College Preparation: how test scores are read,
//    the two intended majors, residency priority, and the audition for
//    music or theatre concentrations.
//  - Clemson Student Financial Aid, Estimated Cost of Attendance,
//    2026-2027 undergraduate budget, South Carolina resident and non-
//    resident, "Undergrad On or Off Campus" column. The items sum to the
//    page's own totals ($38,198 and $66,180), loan fees included.
//  - College Scorecard, school id 217882 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Clemson Common Data Set 2025-2026 (Clemson OPEN)',
        url: 'https://open.clemson.edu/cds/18',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Clemson Admissions, Apply to Clemson as a First-Year Student',
        url: 'https://www.clemson.edu/admissions/undergraduate-admissions/apply/first-year.html',
        read: '2026-10-01',
    },
    prep: {
        label: 'Clemson Admissions, College Preparation',
        url: 'https://www.clemson.edu/admissions/undergraduate-admissions/discover/college-preparation-information.html',
        read: '2026-10-01',
    },
    costs: {
        label: 'Clemson Student Financial Aid, Estimated Cost of Attendance 2026-2027',
        url: 'https://www.clemson.edu/financial-aid/cost/estimated-cost-of-attendance.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?217882-Clemson-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'clemson-university',
    kbId: 'clemson_university',
    name: 'Clemson University',
    shortName: 'Clemson',
    location: 'Clemson, SC',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Clemson brand guide, web colors); decoration only.
    branding: {
        primary: '#F56600',
        secondary: '#522D80',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/clemson_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 64805,
        admitted: 27498,
        enrolled: 5084,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 10697, admitted: 5661 },
            { group: 'Out-of-state', applied: 52651, admitted: 21277 },
            { group: 'International', applied: 1457, admitted: 560 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Clemson\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Clemson has no early decision plan. Its early action is not binding and not restrictive. The Common Data Set gives no early action counts, so there is no early round acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You name two intended majors; if you are not admissible in your first choice, Clemson considers your second. Requirements can vary by major, and South Carolina residents get priority. One program asks for more:',
        rows: [
            { school: 'Performing Arts, music or theatre concentration', requirement: 'An audition is required.' },
        ],
        source: 'prep',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular decision', value: 'January 1' },
        rows: [
            { plan: 'Early Action', deadline: 'October 15', decision: 'Mid-December', binding: false },
            { plan: 'Regular Decision', deadline: 'January 1', decision: 'Mid-February', binding: false },
            { plan: 'Final date to apply', deadline: 'May 1', decision: null, binding: false },
        ],
        note: 'The application opens August 1. Outstanding checklist items are due November 1 for Early Action and January 10 for Regular Decision. Early Action applicants get an admit or deny decision or move to Regular Decision. For scholarship consideration, apply by January 1 and complete your file by January 10. Admitted students have until May 1 to decide. Clemson gives no decision date for applications sent after January 1.',
        source: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Clemson is test optional: you choose whether your SAT or ACT scores are used, and you can switch that choice until the materials deadline (November 1 for Early Action, January 10 for Regular Decision). Scores must come officially from the testing agency. Clemson superscores both the SAT and the ACT.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 admission but considered if submitted, with scores due by May 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [14038, 42020] },
            { item: 'Fees (mandatory, with average lab fees)', amounts: [1832, 1832] },
            { item: 'Housing (average residence hall)', amounts: [9304, 9304] },
            { item: 'Food (unlimited meal plan)', amounts: [5576, 5576] },
            { item: 'Books, supplies and course materials', amounts: [1576, 1576] },
            { item: 'Transportation', amounts: [1320, 1320] },
            { item: 'Personal', amounts: [4484, 4484] },
            { item: 'Loan fees (average federal)', amounts: [68, 68] },
        ],
        totals: [38198, 66180],
        note: 'Clemson\'s budget for a full-time undergraduate, fall and spring, on or off campus. First-year students add a $2,524 laptop allowance. Some majors add an enrichment fee of $0 to $2,500, not included here.',
        source: 'costs',
        netPrice: {
            average: 22032,
            byIncome: [
                { income: '$0 to $30,000', amount: 14943 },
                { income: '$30,001 to $48,000', amount: 14638 },
                { income: '$48,001 to $75,000', amount: 16669 },
                { income: '$75,001 to $110,000', amount: 22127 },
                { income: 'Over $110,000', amount: 27922 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Clemson\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
